/**
 * deliveryGroupSummary — shared LS/GS field accessors (#1289 C2).
 *
 * The feed view and the canvas must derive identical numbers, dates and
 * money figures for a match group, so the canvas's accessors moved here
 * (``formatDate`` + ``headerDateValue`` relocated verbatim from
 * MatchGraph; ``eurFmt`` consolidated from its FilterBar/MatchGraph
 * duplicates). MatchGraph re-exports ``headerDateValue`` so the #839
 * unit test keeps importing from there.
 */
import type { MatchGroup } from '../../../hooks/useMatchGroups';
import { isEventFlagged, eventFlagComment } from './resolvedExclusion';

export function formatDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return iso;
  }
}

/**
 * #839 — Pick the header-date source value for an event tile.
 *
 * The Gutschrift-Check tile header carries the document/issue date —
 * "when the document was printed". Settlement (GS) tiles read
 * ``event_date`` or ``credit_date``; that branch is unchanged.
 *
 * Delivery (LS) tiles previously read the same ``event_date ??
 * delivery_date`` chain, but the extractor's ``event_date``
 * canonicalisation mapped from ``delivery_date`` (= Liefertermin,
 * pre-#839), so the LS tile header rendered the Liefertermin where
 * every other tile rendered the issue date. After the extractor
 * split, LS events carry ``document_date`` as a distinct attribute;
 * this helper prefers it on the delivery side and falls back to the
 * legacy chain for rows extracted before the split — those rows
 * still display *some* date instead of crashing or rendering "—".
 *
 * Exported for the unit test that pins the fallback order; kept
 * adjacent to ``formatDate`` since both are header-date concerns.
 */
export function headerDateValue(
  side: 'delivery' | 'settlement',
  event: Record<string, unknown>,
): string | undefined {
  if (side === 'delivery') {
    const v =
      (event.document_date as string | undefined) ??
      (event.event_date as string | undefined) ??
      (event.delivery_date as string | undefined);
    return typeof v === 'string' && v.length > 0 ? v : undefined;
  }
  const v =
    (event.event_date as string | undefined) ??
    (event.delivery_date as string | undefined) ??
    (event.credit_date as string | undefined) ??
    (event.settlement_date as string | undefined);
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

/**
 * GS date for feed sorting/summary — Decision 3: the issue spec's
 * ``document_date`` prepended to the canvas's #839 settlement chain (a
 * strict superset; the canvas's own header rendering is untouched).
 * Divergence window: a GS event carrying both ``document_date`` and a
 * differing ``event_date`` is rare since on GS events ``event_date``
 * IS the issue date per #839.
 */
export function gsDateValue(event: Record<string, unknown>): string | undefined {
  const doc = event.document_date;
  if (typeof doc === 'string' && doc.length > 0) return doc;
  return headerDateValue('settlement', event);
}

/** de-DE EUR formatter — single copy (was duplicated in FilterBar + MatchGraph). */
export const eurFmt = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
});

function readNumber(ev: Record<string, unknown>, name: string): number | null {
  const raw = ev[name];
  if (raw == null) return null;
  const num = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(num) ? num : null;
}

export interface DeliveryGroupSideSummary {
  /** Document number per the canvas chain; '—' when nothing usable. */
  number: string;
  /** Raw ISO date (sortable lexicographically); undefined when undated. */
  dateISO: string | undefined;
  /** de-DE DD.MM.YYYY; '—' when undated. */
  dateFormatted: string;
  /** Side money: GS = engine settlement_total ?? event total_amount;
   *  LS = engine delivery_total ONLY (LS events carry no total_amount). */
  money: number | null;
}

export interface DeliveryGroupSummary {
  ls: DeliveryGroupSideSummary | null;
  gs: DeliveryGroupSideSummary | null;
}

/** LS document number per the canvas chain; '—' when nothing usable. */
function lsNumber(de: Record<string, unknown>): string {
  return String(de.delivery_number ?? de.delivery_note_number ?? de.entity_id ?? '—');
}

/** GS document number per the canvas chain; '—' when nothing usable. */
function gsNumber(se: Record<string, unknown>): string {
  return String(se.number ?? se.credit_note_number ?? se.settlement_number ?? se.entity_id ?? '—');
}

export function deliveryGroupSummary(group: MatchGroup): DeliveryGroupSummary {
  const de = group.deliveryEvent;
  const se = group.settlementEvent;

  // #1400 — the folded summary now carries ONLY the primary document
  // number (the ``+N`` aggregateSuffix is retired). The per-document
  // breakdown is ``deliveryGroupBadges``; this folded summary stays the
  // single source for side money (engine COMPONENT totals) and for the
  // legacy single-balloon shape the canvas/tests still read.
  let ls: DeliveryGroupSideSummary | null = null;
  if (de) {
    const dateISO = headerDateValue('delivery', de);
    ls = {
      number: lsNumber(de),
      dateISO,
      dateFormatted: formatDate(dateISO),
      money: group.money?.deliveryTotal ?? null,
    };
  }

  let gs: DeliveryGroupSideSummary | null = null;
  if (se) {
    const dateISO = gsDateValue(se);
    gs = {
      number: gsNumber(se),
      dateISO,
      dateFormatted: formatDate(dateISO),
      money: group.money?.settlementTotal ?? readNumber(se, 'total_amount'),
    };
  }

  return { ls, gs };
}

/**
 * #1400 — one badge descriptor per document.
 *
 * The feed previously folded a multi-event side behind ``LS#… +N`` /
 * ``GS#… +N``. That hid the individual documents (and, with #1397, made
 * them un-clickable). ``deliveryGroupBadges`` expands each side into one
 * descriptor PER event — ``allDeliveryEvents`` / ``allSettlementEvents``
 * — reusing the same number/date chains as the folded summary so feed
 * and canvas still agree per document. Each descriptor carries its
 * source ``entityId`` so #1397 can open the right document preview.
 *
 * Money is deliberately NOT per-document here: ``group.money`` is the
 * engine's COMPONENT total for the whole side, not attributable to a
 * single document, so it stays on the folded ``deliveryGroupSummary``
 * and is rendered once per side.
 *
 * Falls back to the primary ``deliveryEvent`` / ``settlementEvent`` when
 * the ``all*Events`` array is empty (single-document groups, and the
 * materialisation paths that only populate the primary event).
 */
export interface DeliveryGroupDocBadge {
  /** Source EVENT id — the #1400 ``data-entity-id`` contract (NOT the
   *  preview target; see ``docId``). */
  entityId: string | undefined;
  /**
   * #1397 — the event's ``source_doc_id``: the preview target the feed
   * hands to ``DocumentPreviewPanel`` (which downloads
   * ``/user-documents/<docId>/download``). This is the SAME id the
   * canvas's MatchGraph emits via ``onNodeClick`` (``resolvedEvent
   * .source_doc_id``), so feed and canvas open identical documents.
   * ``undefined`` when the event carries no source document — that badge
   * stays non-clickable (no preview target), mirroring the canvas's
   * ``source_doc_id ?? null`` guard.
   */
  docId: string | undefined;
  /** Document number per the side's canvas chain; '—' when nothing usable. */
  number: string;
  /** Raw ISO date (sortable lexicographically); undefined when undated. */
  dateISO: string | undefined;
  /** de-DE DD.MM.YYYY; '—' when undated. */
  dateFormatted: string;
  /**
   * #1396 — operator flagged this document (``flagged_at`` set on the
   * event). Shown to the end-user as a flag marker on the balloon. The
   * flag is NON-destructive: a flagged document still renders normally,
   * it only gains the marker.
   */
  isFlagged: boolean;
  /**
   * #1396 — the reviewer's free-text note (``flag_comment``), surfaced as
   * the flag marker's tooltip so the farmer sees *why* the document was
   * flagged. ``null`` for a bare flag (no note) — the marker then shows a
   * generic label.
   */
  flagComment: string | null;
}

export interface DeliveryGroupBadges {
  ls: DeliveryGroupDocBadge[];
  gs: DeliveryGroupDocBadge[];
}

function entityIdOf(ev: Record<string, unknown>): string | undefined {
  const id = ev.entity_id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/** #1397 — the event's source document id (the preview target). Mirrors
 *  MatchGraph's ``resolvedEvent.source_doc_id`` read. */
function docIdOf(ev: Record<string, unknown>): string | undefined {
  const id = ev.source_doc_id;
  return typeof id === 'string' && id.length > 0 ? id : undefined;
}

/** Prefer the full per-event set; fall back to the primary event so
 *  single-document groups (``all*Events`` empty) still render one badge. */
function sideEvents(
  all: Record<string, unknown>[],
  primary: Record<string, unknown> | null,
): Record<string, unknown>[] {
  if (all.length > 0) return all;
  return primary ? [primary] : [];
}

export function deliveryGroupBadges(group: MatchGroup): DeliveryGroupBadges {
  const ls = sideEvents(group.allDeliveryEvents, group.deliveryEvent).map((de) => {
    const dateISO = headerDateValue('delivery', de);
    return {
      entityId: entityIdOf(de),
      docId: docIdOf(de),
      number: lsNumber(de),
      dateISO,
      dateFormatted: formatDate(dateISO),
      // #1396 — flag state read off the event; no overrides in the feed
      // (the persisted attribute is the source of truth for the end-user).
      isFlagged: isEventFlagged(de),
      flagComment: eventFlagComment(de),
    };
  });

  const gs = sideEvents(group.allSettlementEvents, group.settlementEvent).map((se) => {
    const dateISO = gsDateValue(se);
    return {
      entityId: entityIdOf(se),
      docId: docIdOf(se),
      number: gsNumber(se),
      dateISO,
      dateFormatted: formatDate(dateISO),
      isFlagged: isEventFlagged(se),
      flagComment: eventFlagComment(se),
    };
  });

  return { ls, gs };
}
